import { InputViewBox } from "@biggeo/bg-server-lib/studio";
import {
    WithPartialValues,
    WithRequiredProperty,
    isObjectNotEmpty,
} from "@biggeo/bg-utils";
import * as A from "fp-ts/lib/Array";
import { pipe } from "fp-ts/lib/function";
import isArray from "lodash/isArray";
import isEmpty from "lodash/isEmpty";
import isEqual from "lodash/isEqual";
import isNil from "lodash/isNil";
import isUndefined from "lodash/isUndefined";
import omitBy from "lodash/omitBy";
import uniqWith from "lodash/uniqWith";
import { createContext, useContext, useEffect } from "react";
import { v4 as uuid } from "uuid";

import { MapTheme, SubscriptionResponse } from "@biggeo/bg-server-lib/studio";
import { RemoteData } from "@vividtheory/remotedata";
import { AuroraError } from "../../../velocity/aurora/common";
import { DataRspSuccess } from "../../../velocity/aurora/map";
import { DrawEventType } from "../../utils/draw-modes-types";
import { getMapFeatures } from "../../utils/geometry-utils";
import { setCustomShapeStyling } from "../../utils/style-utils";
import { FunctionType } from "../../utils/type";
import { updateArray } from "../../utils/utils";
import { DEFAULT_MAP_STYLE } from "../../views/MapStyles";
import { DEFAULT_VIEWPORT } from "../utils/map-utils";
import {
    DrawType,
    MapContextDataset,
    MapContextPolygon,
    MapState,
    MapType,
    MapUpdateResponse,
} from "./types";

export const MAP_STATES_LIMIT = 10;

export type MapContextType = {
    readonly map: MapType;
    readonly draw: DrawType;
    readonly isMapLoaded: boolean;
    readonly isLoading: {
        data: boolean;
        boundaries: boolean;
    };
    readonly polygons: MapContextPolygon[];
    readonly viewport: InputViewBox;
    readonly functionType: FunctionType;
    readonly modes?: {
        [MapModes.select]: {
            isSelectMode: boolean;
            selectMode: (s: boolean) => void;
        };
    };
    readonly dispatch?: React.Dispatch<MapAction>;
    readonly mapState?: MapState;
    readonly mapStyle?: MapTheme;
    readonly datasets: MapContextDataset[];
    // /**
    //  * Holds the responses from bg-velocity containing geometries to be displayed on the map
    //  * The responseId is used to determine when this object has been updated, to update displays
    //  */
    readonly socketResponses: {
        readonly responses: MapUpdateResponse;
        readonly filteredDataResponse?: Record<
            string,
            RemoteData<AuroraError, DataRspSuccess>
        >;
        readonly responseId?: string;
    };
};

export enum MapModes {
    draw = "draw",
    select = "select",
}

export const mapInitialState: MapState = {
    id: undefined,
    map: { current: null },
    draw: { current: null },
    featureCollection: {
        type: "FeatureCollection",
        features: [],
    },
    datasets: [],
    polygons: [],
    modes: {
        draw: false,
        select: false,
    },
    functionType: FunctionType.viewport,
    viewport: undefined,
    styleUrl: DEFAULT_MAP_STYLE.url,
};

export const MapContext = createContext<MapContextType>({
    map: { current: null },
    draw: { current: null },
    isMapLoaded: false,
    isLoading: { data: false, boundaries: false },
    dispatch: undefined,
    mapState: mapInitialState,
    modes: undefined,
    mapStyle: undefined,
    datasets: [],
    viewport: DEFAULT_VIEWPORT,
    polygons: [],
    functionType: FunctionType.viewport,
    socketResponses: {
        responses: {},
    },
});

export const useMap = (): MapContextType => useContext(MapContext);

export type MapAction =
    | {
          readonly type: "SET_MAP";
          readonly values: Partial<MapContextType>;
      }
    | {
          readonly type: "ADD_POLYGONS";
          readonly values: MapContextPolygon | MapContextPolygon[];
      }
    | {
          readonly type: "UPDATE_POLYGON";
          readonly values: WithRequiredProperty<
              Partial<MapContextPolygon>,
              "id"
          >;
      }
    | {
          readonly type: "OVERWRITE_POLYGONS";
          readonly values: MapContextPolygon[];
      }
    | {
          readonly type: "DELETE_POLYGONS";
          readonly values: { ids: readonly string[] };
      }
    | {
          readonly type: "SET_PREVIOUS_STATE";
          readonly values: {
              feature?: GeoJSON.Feature<
                  GeoJSON.Geometry,
                  GeoJSON.GeoJsonProperties
              >;
              featureCollection?: GeoJSON.FeatureCollection<
                  GeoJSON.Geometry,
                  GeoJSON.GeoJsonProperties
              >;
              state?: Partial<MapState>;
              type?:
                  | "draw.create"
                  | "draw.update"
                  | "draw.delete"
                  | "draw.render";
          };
      }
    | {
          type: "SET_MAP_STYLE";
          values: MapTheme | undefined;
      }
    | {
          type: "ADD_DATASETS";
          values: MapContextDataset | MapContextDataset[];
      }
    | {
          type: "UPDATE_DATASET";
          values: {
              dataSourceId: string;
              dataset: Partial<WithPartialValues<MapContextDataset>>;
          };
      }
    | {
          type: "REMOVE_DATASET";
          values: {
              dataSourceId: string;
              mapTemplateDatasetId?: string;
          };
      }
    | {
          type: "OVERRIDE_DATASETS";
          values: MapContextDataset[];
      }
    | {
          type: "REORDER_DATASETS";
          values: {
              dataSourceIds: string[];
          };
      }
    | {
          type: "SET_LOADING";
          values: {
              state: Partial<{ data: boolean; boundaries: boolean }>;
          };
      }
    | {
          type: "SET_SOCKET_RESPONSES";
          values: SubscriptionResponse;
      }
    | {
          type: "SET_SOCKET_FILTERED_DATA_RESPONSE";
          values: {
              databaseId: string;
              data: RemoteData<AuroraError, DataRspSuccess>;
          };
      }
    | {
          type: "CLEAR_SOCKET_RESPONSES";
      }
    | {
          type: "CLEAR_POLYGONS";
      };

export const MapReducer = (
    state: MapContextType,
    action: MapAction
): MapContextType => {
    switch (action.type) {
        case "SET_MAP": {
            return {
                ...state,
                ...omitBy(action.values, isNil),
            };
        }

        case "ADD_POLYGONS": {
            return {
                ...state,
                polygons: isArray(action.values)
                    ? [...state.polygons, ...action.values]
                    : [...state.polygons, action.values],
            };
        }
        case "UPDATE_POLYGON": {
            return {
                ...state,
                polygons: state.polygons.map((polygon) =>
                    polygon.id === action.values.id
                        ? { ...polygon, ...action.values }
                        : polygon
                ),
            };
        }
        case "OVERWRITE_POLYGONS": {
            return {
                ...state,
                polygons: action.values,
            };
        }
        case "CLEAR_POLYGONS": {
            return {
                ...state,
                polygons: [],
            };
        }
        case "DELETE_POLYGONS": {
            return {
                ...state,
                polygons: state.polygons.filter(
                    (p) => !action.values.ids.includes(p.id)
                ),
            };
        }
        case "SET_PREVIOUS_STATE": {
            const feature = action.values.feature;
            const featureCollection = action.values.featureCollection;
            const draw = action.values.state?.draw || state.draw;

            if (!action.values.type && !feature && featureCollection) {
                return {
                    ...state,
                    mapState: state.mapState
                        ? {
                              ...state.mapState,
                              featureCollection,
                          }
                        : state.mapState,
                };
            }

            if (action.values.type === "draw.delete" && feature) {
                return {
                    ...state,
                    mapState: {
                        ...(state.mapState as MapState),
                        ...action.values.state,
                        featureCollection: draw
                            ? {
                                  type: "FeatureCollection",
                                  features: pipe(
                                      getMapFeatures(draw, state.isMapLoaded),
                                      A.filter((f) => f.id !== feature.id)
                                  ),
                              }
                            : state.mapState?.featureCollection,
                    },
                };
            }

            return {
                ...state,
                mapState: {
                    ...(state.mapState as MapState),
                    ...action.values.state,
                    featureCollection: draw
                        ? {
                              type: "FeatureCollection",
                              features: feature
                                  ? updateArray(
                                        feature,
                                        getMapFeatures(draw, state.isMapLoaded)
                                    )
                                  : getMapFeatures(draw, state.isMapLoaded),
                          }
                        : state.mapState?.featureCollection,
                },
            };
        }
        case "SET_MAP_STYLE": {
            return {
                ...state,
                mapStyle: action.values,
            };
        }
        case "ADD_DATASETS": {
            const payload = action.values;

            if (isArray(payload)) {
                return {
                    ...state,
                    datasets: pipe(
                        uniqWith(
                            pipe(state.datasets, A.concat(payload)),
                            (x, y) => isEqual(x.dataSource.id, y.dataSource.id)
                        ),
                        A.map((p) => {
                            const found = state.datasets.find(
                                (f) => f.dataSource.id === p.dataSource.id
                            );

                            if (found) {
                                const {
                                    dataSource,
                                    mapTemplateDataset,
                                    ...params
                                } = found;

                                return {
                                    ...p,
                                    dataSource: {
                                        ...p.dataSource,
                                        ...found.dataSource,
                                    },
                                    mapTemplateDataset: found.mapTemplateDataset
                                        ? {
                                              ...p.mapTemplateDataset,
                                              ...found.mapTemplateDataset,
                                          }
                                        : p.mapTemplateDataset,
                                    ...omitBy(params, isNil),
                                };
                            }

                            return p;
                        })
                    ),
                };
            }

            return {
                ...state,
                datasets: uniqWith(
                    pipe(state.datasets, A.concat([payload])),
                    (x, y) => isEqual(x.dataSource.id, y.dataSource.id)
                ),
            };
        }
        case "UPDATE_DATASET": {
            const { dataSourceId, dataset } = action.values;
            const {
                dataSource,
                mapTemplateDataset,
                configuration,
                dataSourceConfiguration,
                ...params
            } = dataset;
            const hasSelectedProp =
                !isUndefined(params.isSelected) &&
                isEqual(params.isSelected, true);
            const hasTableViewedProp = !isUndefined(params.isTableViewed);
            const hasConfigurationProp = !isUndefined(configuration);

            const viewedDatasets = pipe(
                state.datasets,
                A.filter((d) => isEqual(d.isTableViewed, true))
            );

            const isTableOpen = !isEmpty(viewedDatasets);

            return {
                ...state,
                datasets: pipe(
                    state.datasets,
                    A.map((d) =>
                        d.dataSource.id === dataSourceId
                            ? {
                                  ...d,
                                  dataSourceConfiguration:
                                      dataSourceConfiguration &&
                                      isObjectNotEmpty(dataSourceConfiguration)
                                          ? {
                                                ...d.dataSourceConfiguration,
                                                ...omitBy(
                                                    dataSourceConfiguration,
                                                    isNil
                                                ),
                                            }
                                          : d.dataSourceConfiguration,
                                  userDataSource: dataSource
                                      ? {
                                            ...d.dataSource,
                                            ...dataSource,
                                        }
                                      : d.dataSource,
                                  mapTemplateDataset:
                                      mapTemplateDataset &&
                                      isObjectNotEmpty(mapTemplateDataset)
                                          ? {
                                                ...d.mapTemplateDataset,
                                                ...mapTemplateDataset,
                                                styles: {
                                                    ...d.mapTemplateDataset
                                                        ?.styles,
                                                    ...mapTemplateDataset.styles,
                                                },
                                            }
                                          : d.mapTemplateDataset,
                                  ...(hasConfigurationProp && {
                                      configuration: {
                                          ...d.configuration,
                                          ...omitBy(configuration, isNil),
                                          options: {
                                              ...d.configuration.options,
                                              ...omitBy(
                                                  configuration.options,
                                                  isNil
                                              ),
                                          },
                                      },
                                  }),

                                  ...omitBy(params, isNil),
                                  ...(hasTableViewedProp && {
                                      tableOrder: viewedDatasets.length,
                                  }),
                                  ...(isTableOpen &&
                                      hasSelectedProp && {
                                          isTableViewed: true,
                                          tableOrder: viewedDatasets.length,
                                      }),
                              }
                            : d
                    )
                ),
            };
        }
        case "REMOVE_DATASET": {
            const { dataSourceId } = action.values;

            return {
                ...state,
                datasets: pipe(
                    state.datasets,
                    A.filter(
                        (dataset) => dataset.dataSource.id !== dataSourceId
                    )
                ),
            };
        }
        case "OVERRIDE_DATASETS": {
            return {
                ...state,
                datasets: action.values,
            };
        }
        case "REORDER_DATASETS": {
            return {
                ...state,
                datasets: state.datasets.sort(
                    (a, b) =>
                        action.values.dataSourceIds.indexOf(a.dataSource.id) -
                        action.values.dataSourceIds.indexOf(b.dataSource.id)
                ),
            };
        }
        case "SET_LOADING": {
            return {
                ...state,
                isLoading: {
                    ...state.isLoading,
                    ...action.values.state,
                },
            };
        }

        case "SET_SOCKET_RESPONSES": {
            return {
                ...state,
                socketResponses: {
                    ...state.socketResponses,
                    responses: {
                        ...state.socketResponses.responses,
                        [action.values.databaseId]: action.values,
                    },
                    responseId: uuid(),
                },
            };
        }
        case "SET_SOCKET_FILTERED_DATA_RESPONSE": {
            return {
                ...state,
                socketResponses: {
                    ...state.socketResponses,
                    filteredDataResponse: {
                        ...state.socketResponses.filteredDataResponse,
                        [action.values.databaseId]: action.values.data,
                    },
                },
            };
        }
        case "CLEAR_SOCKET_RESPONSES": {
            return {
                ...state,
                socketResponses: {
                    responses: {},
                },
            };
        }
        default:
            return state;
    }
};

export const useMapState = ({
    isMapLoaded,
    state,
    dispatch,
    callbacks,
}: {
    isMapLoaded: boolean;
    state: MapState;
    dispatch?: React.Dispatch<MapAction>;
    callbacks?: {
        deletePolygon: (id: string) => void;
    };
}) => {
    const {
        map,
        draw,
        datasets,
        modes,
        featureCollection,
        polygons,
        ...params
    } = state;

    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    useEffect(() => {
        const handleMapReset = (e?: DrawEventType) => {
            if (isMapLoaded) {
                const feature = e ? e.features[0] : undefined;
                const id = feature?.id ? feature.id.toString() : undefined;

                if (e && e.type === "draw.delete" && callbacks && id) {
                    callbacks.deletePolygon(id);
                }

                dispatch?.({
                    type: "SET_PREVIOUS_STATE",
                    values: {
                        type: e ? e.type : undefined,
                        feature,
                        state: {
                            map,
                            draw,
                            datasets,
                            modes,
                            polygons,
                            ...omitBy(params, isNil),
                        },
                    },
                });
            }
        };

        if (map.current) {
            map.current.on("draw.create", handleMapReset);
            map.current.on("draw.update", handleMapReset);
            map.current.on("draw.delete", handleMapReset);

            handleMapReset();
        }

        return () => {
            if (map.current) {
                map.current.off("draw.create", handleMapReset);
                map.current.off("draw.update", handleMapReset);
                map.current.off("draw.delete", handleMapReset);
            }
        };
    }, [
        map.current,
        draw.current,
        isMapLoaded,
        datasets.length,
        params.functionType,
        params.styleUrl,
        params.viewport?.view,
        modes.draw,
        modes.select,
        polygons.length,
    ]);
};

export const onMapReset = ({
    map,
    draw,
    state,
    setState,
    polygons: OGPolygons,
}: {
    map: mapboxgl.Map;
    draw?: MapboxDraw;
    state: MapState;
    polygons: MapContextPolygon[];
    setState: {
        setDatasets?: (d: MapContextDataset[]) => void;
        setPolygons: (polygons: MapContextPolygon[]) => void;
        setFunctionType: (f: FunctionType) => void;
        setMode: (m: boolean) => void;
        setViewport: (
            v: Partial<{
                bounds: mapboxgl.LngLatBounds;
                center: mapboxgl.LngLat;
                view: InputViewBox;
                zoom: number;
            }>
        ) => void;
        setStyle?: (url: string) => void;
    };
}) => {
    const {
        setDatasets,
        setPolygons,
        setFunctionType,
        setViewport,
        setStyle,
        setMode,
    } = setState;

    const {
        featureCollection,
        functionType,
        viewport,
        polygons,
        styleUrl,
        datasets,
        modes,
    } = state;

    map.on("load", () => {
        if (featureCollection && draw) {
            draw.set(featureCollection);

            setTimeout(() => {
                setCustomShapeStyling({
                    map,
                    isMapLoaded: true,
                    datasets,
                    geometries: featureCollection.features,
                });
            }, 200);
        }

        if (styleUrl && setStyle) {
            const currentStyle = map.getStyle();

            if (!isEqual(currentStyle.sprite, styleUrl)) {
                setStyle(styleUrl);
            }
        }

        const drawnShapes = draw?.getAll().features;

        if (!!drawnShapes && !isEmpty(drawnShapes)) {
            // Wait till the shapes are drawn, then set the polygons state
            setPolygons(polygons);
        }

        if (
            viewport &&
            isEmpty(drawnShapes) &&
            isEmpty(featureCollection?.features)
        ) {
            setViewport(viewport);
        }

        if (!isEmpty(polygons)) {
            // Wait till the polygons state is set, then fetch the datasets
            if (!isEmpty(OGPolygons)) {
                setDatasets?.(datasets);
            }
        } else {
            setDatasets?.(datasets);
        }
    });

    map.on("style.load", () => {
        if (functionType) {
            setFunctionType(functionType);
        }

        setMode(modes.select);
    });
};
